import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { StripeProvider, Elements } from 'react-stripe-elements';

import PowerCheckoutForm from '../../PowerCheckoutForm';
import SplashBall from '../../Splash/splashBall';
import Img from '../../Img';
import ChangeCardForm from '../../Upgrade/UpgradePowerMonthlyToAnnualPopup/ChangeCardForm';
import usePowerSubscriptionPlans from '../../../hooks/usePowerSubscriptionPlans';
import useMonthlyToAnnualUpgrade from '../../../hooks/useMonthlyToAnnualUpgrade';
import usePaymentUpdate from '../../../hooks/usePaymentUpdate';
import { getSubscriptionInfo, clearCardError } from '../../../actions/checkout-subscription-actions';
import { SUBSCRIBE_MEMBER_CHECKOUT_FAILURE } from '../../../actions/subscribe-action-types';
import { SubscriptionIntervalTypes } from '../../../utils/constants';
import { SubscriptionGateways,
    SubscriptionTerms,
    SubscriptionTypes,
    intervalToBillingCycle,
} from '../../Analytics/constants';
import { createMonthlyUpgradeTelemetry } from '../../Analytics/createMonthlyUpgradeTelemetry';

import { fetchAuthenticatedUser } from '../../../actions';
import config from '../../../../config';
import {
    DEFAULT_CURRENCY,
    SIDEBAR_CHECKOUT_CONTENT,
    CARD_BRAND_ICON_MAP,
    ACCEPTED_CARD_BRANDS,
    MONTHLY_TO_ANNUAL_CONTENT,
    SUCCESS_SCENARIOS,
    SUCCESS_CONTENT,
} from './constants';
import PricingSummary from './PricingSummary';
import PastDueWarning from './PastDueWarning';
import CardOnFileSection from './CardOnFileSection';

import styles from './sidebarPowerCheckout.scss';

const changeCardFormStyles = {
    formTitle: styles.changeCardInstruction,
    changeCardLink: styles.changeCardLink,
    upgradeButton: `btn btn-primary text-uppercase btn-block ${styles.upgradeButton}`,
    disclaimer: styles.disclaimer,
};

const renderCardBrands = () => (
    <div className={styles.cardBrands}>
        <span className={styles.cardBrandsText}>{MONTHLY_TO_ANNUAL_CONTENT.cardBrandsText}</span>
        {ACCEPTED_CARD_BRANDS.map((brand) => (
            <Img key={brand} src={CARD_BRAND_ICON_MAP[brand]} alt={brand} />
        ))}
    </div>
);

const BinCountryError = ({ error }) => {
    if (!error) {
        return null;
    }
    return (
        <div className={styles.binCountryError}>
            <div className={styles.binCountryErrorTitle}>
                {SIDEBAR_CHECKOUT_CONTENT.binCountryError.title}
            </div>
            <div className={styles.binCountryErrorText}>
                {SIDEBAR_CHECKOUT_CONTENT.binCountryError.text}
            </div>
        </div>
    );
};

BinCountryError.propTypes = {
    error: PropTypes.string,
};

BinCountryError.defaultProps = {
    error: '',
};

const Disclaimer = () => (
    <div className={styles.disclaimer}>
        {SIDEBAR_CHECKOUT_CONTENT.disclaimer.prefix}
        <a href={SIDEBAR_CHECKOUT_CONTENT.disclaimer.termsHref} target="_blank" rel="noopener noreferrer">
            {SIDEBAR_CHECKOUT_CONTENT.disclaimer.termsLink}
        </a>
        {SIDEBAR_CHECKOUT_CONTENT.disclaimer.suffix}
    </div>
);

const ErrorDisplay = ({ error }) => {
    if (!error) {
        return null;
    }
    return (
        <div className="is-invalid mb16">
            <div className="invalid-feedback">{error}</div>
        </div>
    );
};

ErrorDisplay.propTypes = {
    error: PropTypes.string,
};

ErrorDisplay.defaultProps = {
    error: '',
};

const StripePlaceholder = () => (
    <div className={styles.pricingSummary}>
        <div className={styles.pricingRow}>
            <div>
                <div className={styles.pricingPlaceholderPrice} />
                <div className={styles.pricingPlaceholderMonthly} />
            </div>
            <div className={styles.pricingPlaceholderRenewal} />
        </div>
    </div>
);

// Classify the current user into one of the four upgrade scenarios this sidebar
// supports. Lives outside the component so the boolean derivations don't count
// against the component's cognitive complexity.
const deriveScenarioFlags = (user) => {
    const isMonthlyStripe = user?.subscriptionTierType === SubscriptionIntervalTypes.MONTH &&
        user?.subscriptionGateway === SubscriptionGateways.STRIPE;
    return {
        isMonthlyStripe,
        isPastDueMonthly: isMonthlyStripe && !!user?.hasPastDueSubscription,
        isMonthlyUser: isMonthlyStripe && !!user?.isPowered && !user?.hasPastDueSubscription,
        isPastDueAnnual: !!user?.hasPastDueSubscription &&
            user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR &&
            user?.subscriptionGateway === SubscriptionGateways.STRIPE,
    };
};

const SidebarPowerCheckout = ({ // NOSONAR — React component name must be PascalCase per JSX convention
    onUpgradeComplete,
    onUpgradeSucceeded,
    referringSurface,
    gatedFeature,
    sourceEventId,
    userSubscriptionStatus,
    setCheckoutSubmittingFlag,
}) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth?.user);
    const isReduxSubmitting = useSelector((state) => state.subscribe?.loading);
    const reduxCardError = useSelector((state) => state.subscribe?.cardError);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [successInfo, setSuccessInfo] = useState(null);
    const isSubmitting = isFormSubmitting || isReduxSubmitting;

    const upgradeTelemetry = createMonthlyUpgradeTelemetry({
        userSubscriptionStatus,
        referringSurface,
        gatedFeature,
        sourceEventId,
    });

    const { isMonthlyStripe, isPastDueMonthly, isMonthlyUser, isPastDueAnnual } = deriveScenarioFlags(user);

    // Reset local submitting flag when checkout completes (success or error)
    useEffect(() => {
        if (!isReduxSubmitting && isFormSubmitting) {
            setIsFormSubmitting(false);
        }
    }, [isReduxSubmitting]);

    // Clear any stale cardError from a previous sidebar session so the user
    // doesn't open the upgrade UI and immediately see last attempt's failure.
    useEffect(() => {
        dispatch(clearCardError());
    }, [dispatch]);

    // ── Scenario 1: Free user hooks ──
    const freeUserHook = usePowerSubscriptionPlans({
        defaultInterval: SubscriptionIntervalTypes.YEAR,
        enabled: !isMonthlyStripe && !isPastDueAnnual,
    });

    // ── Scenario 2 & 3: Monthly user hooks ──
    const monthlyHook = useMonthlyToAnnualUpgrade({ isPastDue: isPastDueMonthly, enabled: isMonthlyStripe });
    const [stripe, setStripe] = useState(null);
    const [showChangeCardForm, setShowChangeCardForm] = useState(false);

    // ── Scenario 4: Past-due annual hooks ──
    const paymentUpdateHook = usePaymentUpdate();
    const subscriptions = useSelector((state) => state.subscribe?.subscriptions);

    const annualPastDueDetails = useMemo(() => {
        if (!isPastDueAnnual || !subscriptions?.length) {
            return null;
        }
        const pastDueSub = subscriptions.find((sub) => sub.isPastDue);
        if (!pastDueSub) {
            return null;
        }
        return {
            renewalDate: pastDueSub.renewalDate,
            cardBrand: pastDueSub.paymentSource?.type,
            last4: pastDueSub.paymentSource?.last4,
            displayAmount: pastDueSub.displayAmount,
            amount: pastDueSub.amount,
            planName: pastDueSub.planName,
        };
    }, [isPastDueAnnual, subscriptions]);

    // Fetch subscription info for past-due annual (same API as Scenario 3)
    useEffect(() => {
        if (isPastDueAnnual) {
            dispatch(getSubscriptionInfo());
        }
    }, [isPastDueAnnual, dispatch]);

    // Mirror in-flight subscription processing up to the parent SideBar so its
    // existing close-block + "PAYMENT_PROCESSING" tooltip apply during Power upgrades
    // too. Without this the user can close the sidebar mid-API, which both confuses
    // the UX and fires a false power_upgrade_flow_abandoned event for a successful purchase.
    const isProcessing = isFormSubmitting ||
        isReduxSubmitting ||
        monthlyHook.submitting ||
        paymentUpdateHook.submitting ||
        paymentUpdateHook.isPolling;

    useEffect(() => {
        setCheckoutSubmittingFlag(isProcessing);
    }, [isProcessing, setCheckoutSubmittingFlag]);

    // Stripe init for Scenarios 2, 3, and 4
    useEffect(() => {
        if (!isMonthlyUser && !isPastDueMonthly && !isPastDueAnnual) {
            return;
        }
        if (window.Stripe) {
            setStripe(window.Stripe(config.stripeKey));
        } else {
            const el = document.querySelector('#stripe-js');
            if (el) {
                el.addEventListener('load', () => setStripe(window.Stripe(config.stripeKey)));
            }
        }
    }, [isMonthlyUser, isPastDueMonthly, isPastDueAnnual]);

    // ── Shared pricing summary props ──
    const freeRenewalDate = useMemo(() => moment().add(1, 'year').format('MMM D, YYYY'), []);

    const pricingSummaryProps = {
        isPastDueAnnual,
        annualPastDueDetails,
        isMonthlyUser,
        isPastDueMonthly,
        monthlyHook,
        freeUserHook,
        freeRenewalDate,
    };

    const binError = (isMonthlyUser || isPastDueMonthly)
        ? monthlyHook.binCountryError
        : freeUserHook.binCountryError;

    // ── Scenario 1: Free user handlers ──
    const handleFreeSuccess = async () => {
        const planData = freeUserHook.getSelectedPlan();
        const billingCycle = intervalToBillingCycle[planData?.interval] || null;

        upgradeTelemetry.trackSucceeded({
            billingCycle,
            currency: planData?.currency || DEFAULT_CURRENCY,
            revenueAmount: planData?.amountPayPerRecurringInterval,
        });
        onUpgradeSucceeded();

        await dispatch(fetchAuthenticatedUser()); // NOSONAR — Redux thunk returns a promise
        setSuccessInfo({
            scenario: SUCCESS_SCENARIOS.FREE_TO_ANNUAL,
            renewalDate: moment().add(1, 'year').format('MMM D, YYYY'),
        });
    };

    const handleFreeUpgradeFailed = (error) => {
        upgradeTelemetry.trackFailed(error);
    };

    // ── Scenario 2: Monthly user handlers ──
    const getMonthlyRenewalDate = () => {
        if (monthlyHook.upgradeData?.renewalDate) {
            return moment(monthlyHook.upgradeData.renewalDate).format('MMM D, YYYY');
        }
        return moment().add(1, 'year').format('MMM D, YYYY');
    };

    const trackMonthlyCheckoutStarted = (plan) => {
        upgradeTelemetry.trackStarted({
            planSelected: SubscriptionTypes.POWER,
            billingCycle: SubscriptionTerms.ANNUAL,
            currency: plan?.currency || DEFAULT_CURRENCY,
            priceLocal: plan?.amountPayPerRecurringInterval,
        });
    };

    const trackMonthlyOrderCompleted = (result, plan) => {
        upgradeTelemetry.trackSucceeded({
            billingCycle: SubscriptionTerms.ANNUAL,
            currency: result?.currency || plan?.currency || DEFAULT_CURRENCY,
            revenueAmount: result?.amountPaid || plan?.amountPayPerRecurringInterval,
        });
    };

    const handleMonthlyUpgradeSuccess = async (result, plan) => {
        trackMonthlyOrderCompleted(result, plan);
        onUpgradeSucceeded();
        await dispatch(fetchAuthenticatedUser()); // NOSONAR — Redux thunk returns a promise
        setSuccessInfo({
            scenario: SUCCESS_SCENARIOS.MONTHLY_TO_ANNUAL,
            renewalDate: getMonthlyRenewalDate(),
        });
    };

    const handleMonthlyUpgradeWithCardOnFile = async () => {
        const plan = monthlyHook.upgradeData?.annualPlan;
        trackMonthlyCheckoutStarted(plan);

        const result = await monthlyHook.performUpgrade(null, stripe);
        if (result?.isSubscribed) {
            await handleMonthlyUpgradeSuccess(result, plan);
        } else if (result?.error) {
            upgradeTelemetry.trackFailed(result.error);
        }
    };

    const handleNewCardSuccess = async (paymentMethodId) => {
        const plan = monthlyHook.upgradeData?.annualPlan;
        trackMonthlyCheckoutStarted(plan);

        const result = await monthlyHook.performUpgrade(paymentMethodId, stripe);
        if (result?.isSubscribed) {
            await handleMonthlyUpgradeSuccess(result, plan);
        } else if (result?.error) {
            upgradeTelemetry.trackFailed(result.error);
        }
    };

    // ── Scenario 4: Past-due annual handlers ──
    const getAnnualRenewalDate = () => {
        if (annualPastDueDetails?.renewalDate) {
            return moment(annualPastDueDetails.renewalDate).add(1, 'year').format('MMM D, YYYY');
        }
        return moment().add(1, 'year').format('MMM D, YYYY');
    };

    const setAnnualSuccessInfo = () => {
        onUpgradeSucceeded();
        setSuccessInfo({
            scenario: SUCCESS_SCENARIOS.PAST_DUE_ANNUAL,
            renewalDate: getAnnualRenewalDate(),
        });
    };

    const handleAnnualPaymentUpdate = async (paymentMethodId) => {
        const result = await paymentUpdateHook.handleUpdatePayment(paymentMethodId);
        if (result?.recovered) {
            setAnnualSuccessInfo();
        }
    };

    const handleRefresh = async () => {
        const result = await paymentUpdateHook.handleRefresh();
        if (result?.recovered) {
            setAnnualSuccessInfo();
        }
    };

    // ── Scenario 4: Payment update active states ──
    // These guards live ABOVE the scenario branching because polling calls
    // fetchAuthenticatedUser(), which flips hasPastDueSubscription → false in
    // Redux mid-poll.  That makes isPastDueAnnual false before the hook can
    // transition to RECOVERED, causing the card form to flash.  Checking the
    // hook phase first avoids that race.
    const { submitting: annualSubmitting, isPolling, pollExhausted, recovered: annualRecovered } = paymentUpdateHook;

    // Each renderXScenario helper is a pure render — it reads state/hooks/handlers
    // from the enclosing closure and returns JSX. Pulling them out drops the parent
    // function's cognitive complexity well below the Sonar threshold, since every
    // inline `&&` / ternary in JSX now scopes to its scenario helper rather than
    // to SidebarPowerCheckout itself.
    const renderSuccessScreen = () => {
        const scenarioContent = SUCCESS_CONTENT[successInfo.scenario];
        return (
            <div className={styles.sidebarPowerCheckout} data-test="success-screen">
                <div className={styles.successContainer}>
                    <h3 className={styles.successTitle}>{scenarioContent.title}</h3>
                    <p className={styles.successMessage}>
                        {(scenarioContent.message || SUCCESS_CONTENT.message)(successInfo.renewalDate)}
                    </p>
                    <button
                        type="button"
                        className={`btn btn-primary text-uppercase btn-block ${styles.upgradeButton}`}
                        onClick={onUpgradeComplete}
                        data-test="success-continue-button"
                        data-testid="event-reg.power-checkout.success-continue-btn"
                    >
                        {SUCCESS_CONTENT.ctaText}
                    </button>
                </div>
            </div>
        );
    };

    const renderPollingState = () => (
        <div className={styles.sidebarPowerCheckout}>
            <div className={styles.pollingContainer} data-test="polling-state">
                <SplashBall size="small" />
                <div className={styles.pollingMessage}>
                    {SIDEBAR_CHECKOUT_CONTENT.pollingMessage}
                </div>
            </div>
        </div>
    );

    const renderPollExhausted = () => (
        <div className={styles.sidebarPowerCheckout}>
            <div className={styles.pollExhaustedContainer} data-test="poll-exhausted-state">
                <div className={styles.pollExhaustedMessage}>
                    {SIDEBAR_CHECKOUT_CONTENT.pollExhaustedMessage}
                </div>
                <button
                    type="button"
                    className={`btn btn-primary text-uppercase btn-block ${styles.refreshButton}`}
                    onClick={handleRefresh}
                    data-test="refresh-button"
                    data-testid="event-reg.power-checkout.refresh-btn"
                >
                    {SIDEBAR_CHECKOUT_CONTENT.pollExhaustedRefresh}
                </button>
            </div>
        </div>
    );

    const renderPastDueAnnualScenario = () => (
        <div className={`${styles.sidebarPowerCheckout} ${annualSubmitting ? styles.submitting : ''}`}>
            <PricingSummary {...pricingSummaryProps} />
            <PastDueWarning details={annualPastDueDetails} />

            {stripe ? (
                <StripeProvider stripe={stripe}>
                    <Elements>
                        <ChangeCardForm
                            onSuccess={handleAnnualPaymentUpdate}
                            onError={(stripeErr) => {
                                if (stripeErr) {
                                    dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: stripeErr } });
                                }
                            }}
                            submitting={annualSubmitting}
                            disabled={!annualPastDueDetails}
                            isTennisAustraliaMember={false}
                            savingsPercentage={0}
                            formStyles={changeCardFormStyles}
                            ctaText={SIDEBAR_CHECKOUT_CONTENT.pastDueAnnualCtaText}
                            renderCardBrands={renderCardBrands}
                            errorMessage={reduxCardError}
                        />
                    </Elements>
                </StripeProvider>
            ) : (
                <StripePlaceholder />
            )}
        </div>
    );

    const renderPastDueMonthlyScenario = () => {
        const plan = monthlyHook.upgradeData?.annualPlan;
        const isTA = monthlyHook.upgradeData?.isTennisAustraliaMember ?? false;
        const isLoading = monthlyHook.loading || !plan;

        return (
            <div className={`${styles.sidebarPowerCheckout} ${monthlyHook.submitting ? styles.submitting : ''}`}>
                <PricingSummary {...pricingSummaryProps} />
                <BinCountryError error={binError} />
                <PastDueWarning details={monthlyHook.pastDueDetails} />

                {stripe ? (
                    <StripeProvider stripe={stripe}>
                        <Elements>
                            <ChangeCardForm
                                onSuccess={handleNewCardSuccess}
                                onError={() => { monthlyHook.clearErrors(); }}
                                submitting={monthlyHook.submitting}
                                disabled={isLoading}
                                isTennisAustraliaMember={isTA}
                                savingsPercentage={plan?.savingsPercentage ?? 0}
                                formStyles={changeCardFormStyles}
                                renderCardBrands={renderCardBrands}
                                errorMessage={reduxCardError || monthlyHook.upgradeError}
                            />
                        </Elements>
                    </StripeProvider>
                ) : (
                    isLoading && <StripePlaceholder />
                )}
            </div>
        );
    };

    const renderMonthlyUserScenario = () => {
        const plan = monthlyHook.upgradeData?.annualPlan;
        const isTA = monthlyHook.upgradeData?.isTennisAustraliaMember ?? false;
        const isLoading = monthlyHook.loading || !plan;

        return (
            <div className={`${styles.sidebarPowerCheckout} ${monthlyHook.submitting ? styles.submitting : ''}`}>
                <PricingSummary {...pricingSummaryProps} />
                <BinCountryError error={binError} />
                {!showChangeCardForm && (
                    <ErrorDisplay error={reduxCardError || monthlyHook.upgradeError} />
                )}

                <div className={styles.prorationMessage}>
                    {MONTHLY_TO_ANNUAL_CONTENT.prorationMessage}
                </div>

                <hr className={styles.divider} />

                {showChangeCardForm && stripe ? (
                    <StripeProvider stripe={stripe}>
                        <Elements>
                            <ChangeCardForm
                                onSuccess={handleNewCardSuccess}
                                onError={() => { monthlyHook.clearErrors(); }}
                                submitting={monthlyHook.submitting}
                                isTennisAustraliaMember={isTA}
                                savingsPercentage={plan?.savingsPercentage ?? 0}
                                formStyles={changeCardFormStyles}
                                onUseCardOnFile={() => {
                                    monthlyHook.clearErrors();
                                    dispatch(clearCardError());
                                    setShowChangeCardForm(false);
                                }}
                                renderCardBrands={renderCardBrands}
                                errorMessage={reduxCardError || monthlyHook.upgradeError}
                            />
                        </Elements>
                    </StripeProvider>
                ) : (
                    <>
                        <div className={styles.paymentMethodSection}>
                            <div className={styles.paymentMethodTitle}>
                                {MONTHLY_TO_ANNUAL_CONTENT.paymentMethodTitle}
                            </div>
                            <div className={styles.paymentCard}>
                                <CardOnFileSection
                                    paymentSource={monthlyHook.upgradeData?.paymentSource}
                                    isLoading={isLoading}
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.changeCardLink}
                                onClick={() => {
                                    monthlyHook.clearErrors();
                                    dispatch(clearCardError());
                                    setShowChangeCardForm(true);
                                }}
                                disabled={isLoading}
                                data-testid="event-reg.power-checkout.change-card-btn"
                            >
                                {MONTHLY_TO_ANNUAL_CONTENT.changeCard}
                            </button>
                        </div>

                        <button
                            type="button"
                            className={`btn btn-primary text-uppercase btn-block ${styles.upgradeButton} ${monthlyHook.submitting ? 'text-dark' : ''}`}
                            onClick={handleMonthlyUpgradeWithCardOnFile}
                            disabled={isLoading || monthlyHook.submitting}
                            data-testid="event-reg.power-checkout.upgrade-btn"
                        >
                            {monthlyHook.submitting && (<i className="fas fa-circle-notch fa-spin mr8" />)}
                            {monthlyHook.submitting
                                ? MONTHLY_TO_ANNUAL_CONTENT.ctaProcessing
                                : MONTHLY_TO_ANNUAL_CONTENT.ctaText}
                        </button>

                        <Disclaimer />
                    </>
                )}
            </div>
        );
    };

    const renderFreeUserScenario = () => (
        <div className={`${styles.sidebarPowerCheckout} ${isSubmitting ? styles.submitting : ''}`}>
            <PricingSummary {...pricingSummaryProps} />
            <BinCountryError error={binError} />
            <ErrorDisplay error={reduxCardError} />

            <PowerCheckoutForm
                interval={freeUserHook.interval}
                onComplete={handleFreeSuccess}
                onError={handleFreeUpgradeFailed}
                hideHeader
                themeV3
                hideCardError
                planData={freeUserHook.getSelectedPlan()}
                couponCode={freeUserHook.couponCode}
                couponError={freeUserHook.couponError}
                applyCoupon={freeUserHook.applyCoupon}
                invoiceItems={freeUserHook.invoiceItems}
                onBinCountryMismatch={freeUserHook.handleBinCountryMismatch}
                isLoadingPricing={freeUserHook.isLoadingPricing}
                disabled={freeUserHook.isLoadingPricing || isSubmitting}
                onSubmitStart={() => setIsFormSubmitting(true)}
                submitButtonText={SIDEBAR_CHECKOUT_CONTENT.ctaText}
                isTennisAustraliaMember={freeUserHook.isTennisAustraliaMember}
                savingsPercentage={freeUserHook.getSavingsPercentage()}
                referringSurface={referringSurface}
                gatedFeature={gatedFeature}
                renderCardBrands={renderCardBrands}
                sourceEventId={sourceEventId}
            />

            <Disclaimer />
        </div>
    );

    if (successInfo) {
        return renderSuccessScreen();
    }
    if (annualSubmitting || isPolling || annualRecovered) {
        return renderPollingState();
    }
    if (pollExhausted) {
        return renderPollExhausted();
    }
    if (isPastDueAnnual) {
        return renderPastDueAnnualScenario();
    }
    if (isPastDueMonthly) {
        return renderPastDueMonthlyScenario();
    }
    if (isMonthlyUser) {
        return renderMonthlyUserScenario();
    }
    return renderFreeUserScenario();
};

SidebarPowerCheckout.propTypes = {
    onUpgradeComplete: PropTypes.func.isRequired,
    onUpgradeSucceeded: PropTypes.func,
    referringSurface: PropTypes.string,
    gatedFeature: PropTypes.string,
    sourceEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userSubscriptionStatus: PropTypes.string,
    setCheckoutSubmittingFlag: PropTypes.func,
};

SidebarPowerCheckout.defaultProps = {
    onUpgradeSucceeded: () => {},
    referringSurface: null,
    gatedFeature: null,
    sourceEventId: null,
    userSubscriptionStatus: null,
    setCheckoutSubmittingFlag: () => {},
};

export default SidebarPowerCheckout;
